import Channel from '@@types/Channel';
import { CHANNEL_ID, THREAD_CHANNEL_ID } from '@@types/common';

import { CoachMarkName, MAIN_THREAD_CHANNEL_ID, MemberStatus, MODAL_NAMES, PANEL_TYPE, PopperType } from '~/constants';
import { isArchivedChannel, isUnreadChannel } from '~/services/channelService';
import GA from '~/services/GA/GA';
import globalEventService, { EVENT_NAME } from '~/services/globalEventService';
import Actions from '~/stores/Actions';
import LogActions from '~/stores/actions/LogActions';
import { animationEffectStore } from '~/stores/AnimationEffectStore';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelMembersLangStore } from '~/stores/ChannelMembersLangStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { membersStore } from '~/stores/MembersStore';
import { meteringStore } from '~/stores/MeteringStore';
import { api } from '~/stores/providers/Api2';
import environment from '~/stores/providers/environment';
import logging from '~/stores/providers/logging';
import { stores } from '~/stores/Stores';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { isNative } from '~/utils/runEnvironmentUtil';
import { debounce, isEmpty } from 'lodash';
import { action, comparer, IReactionDisposer, reaction } from 'mobx';
import { isNil, propOr } from 'ramda';
import juxt from 'ramda/es/juxt';
import pipe from 'ramda/es/pipe';

class ObserveActions {
  private currentChannelId: IReactionDisposer;
  private contentsChannelId: IReactionDisposer;
  private threadCurrentChannelId: IReactionDisposer;
  private threadContentsChannelId: IReactionDisposer;
  public hintQuery: IReactionDisposer;
  private screenCapturable: IReactionDisposer;
  private channelUnreadCount: IReactionDisposer;
  private isLoadingLogs: IReactionDisposer;
  private canUseInterTenant: IReactionDisposer;
  private onLine: IReactionDisposer;
  private coachMark: IReactionDisposer;
  private coachMarkHighlightTarget?: () => void;
  private slowFetchingTimer = -1;
  private registeredIdleStatus = false;

  constructor(private actions: Actions) {}

  public observeCurrentChannelId = () => {
    this.currentChannelId?.();
    this.currentChannelId = reaction(() => mainDetailUiState.currentChannelId, this.handleChangeCurrentChannelId);
  };

  public observeContentsChannelId = () => {
    this.contentsChannelId?.();
    this.contentsChannelId = reaction(() => mainDetailUiState.contentsChannelId, this.handleChangeContentsChannelId);
  };

  public observeThreadCurrentChannelId = () => {
    this.threadCurrentChannelId?.();
    this.threadCurrentChannelId = reaction(
      () => threadDetailUiState.currentChannelId,
      this.handleChangeThreadCurrentChannelId
    );
  };

  public observeThreadContentsChannelId = () => {
    this.threadContentsChannelId?.();
    this.threadContentsChannelId = reaction(
      () => threadDetailUiState.contentsChannelId,
      this.handleChangeThreadContentsChannelId
    );
  };

  public observeHintQuery = () => {
    this.hintQuery?.();
    this.hintQuery = reaction(this.actions.hintActions.getHintAndQuery, this.actions.hintActions.handleHintAndQuery, {
      fireImmediately: true,
    });
  };

  public observeCurrentChannelScreenCapturable = () => {
    this.screenCapturable?.();
    this.screenCapturable = reaction(
      () => {
        const dummyParentLogId = threadChannelLogsStore.dummyParentLogId;
        const channelId = dummyParentLogId
          ? channelLogsStore.getById(dummyParentLogId)?.channelId
          : uiState.channelIdByContext;
        return {
          channelId,
          screenCapture: this.actions.channelActions.canUploadChannel(channelManager.getById(channelId)),
        };
      },
      this.handleChangeScreenCapturable,
      { equals: comparer.shallow }
    );
  };

  public observeIsLoadingLogs = () => {
    this.isLoadingLogs?.();
    this.isLoadingLogs = reaction(() => channelLogsStore.isLoadingLogs, this.handleIsLoadingLogs);
  };

  public observeChannelsUnreadCount = () => {
    this.channelUnreadCount?.();
    this.channelUnreadCount = reaction(
      () => channelsStore.unreadCount,
      (count: number) => this.actions.nativeActions.sendDockBadge({ count: Math.max(0, count) }),
      { fireImmediately: true }
    );
  };

  public observeCanUseInterTenant = () => {
    this.canUseInterTenant?.();
    this.canUseInterTenant = reaction(
      () => userStore.canUseInterTenant,
      (value: boolean) => {
        if (value) {
          this.actions.channelActions.fetchAndCacheInterTenantChannels();
        }
      }
    );
  };

  public observeIdleStatus = async () => {
    if (this.registeredIdleStatus || isNative() || !('IdleDetector' in window)) {
      this.registeredIdleStatus = true;
      return;
    }
    this.registeredIdleStatus = true;
    const status = await window.IdleDetector?.requestPermission();
    if (status !== 'granted') {
      return;
    }
    try {
      const controller = new AbortController();
      const signal = controller.signal;

      const idleDetector = new window.IdleDetector();
      idleDetector.addEventListener('change', () => {
        const userState: 'active' | 'idle' = idleDetector.userState;
        const screenState: 'locked' | 'unlocked' = idleDetector.screenState;
        const idle = userState !== 'active' || screenState !== 'unlocked';
        const currentStatus = membersStore.getStatusById(userStore.id);
        if (currentStatus === MemberStatus.AutoAway && !idle) {
          this.actions.userActions.updateStatusFromNative(MemberStatus.Active);
        } else if (currentStatus === MemberStatus.Active && idle) {
          this.actions.userActions.updateStatusFromNative(MemberStatus.AutoAway);
        }
      });

      await idleDetector.start({
        threshold: +environment.idleThreshold,
        signal,
      });
      logging.debug('IdleDetector is active.');
    } catch (err) {
      // Deal with initialization errors like permission denied,
      // running outside of top-level frame, etc.
      logging.error(err.name, err.message);
    }
  };

  public observeOnLine = () => {
    this.onLine?.();
    this.onLine = reaction(
      () => uiState.isOnline,
      (isOnline: boolean) => {
        if (!isOnline) {
          GA.writeCloseWebsocket('OFFLINE');
        }
      }
    );
  };

  public observeCoachMark = () => {
    this.coachMark?.();
    let timeoutId: number = 0;
    this.coachMark = reaction(
      () => {
        const stepName = coachMarkStore.currentStep?.name;
        return {
          stepName,
          target: coachMarkStore.getTarget(stepName),
        };
      },
      ({ stepName, target }: { stepName: CoachMarkName; target: HTMLElement }) => {
        const show = !!stepName && !!target;
        if (!show) {
          window.clearTimeout(timeoutId);
          const popper = uiState.popperMap[PopperType.CoachMark];
          popper?.args.onClickNext();
          uiState.closePopper(PopperType.CoachMark);
          return;
        }
        if (coachMarkStore.currentIndex === 0) {
          coachMarkStore.currentStep?.handlers?.before();
        }

        // log menu 보일 때까지 잠시 대기
        timeoutId = window.setTimeout(() => {
          this.actions.coachMarkActions.openPopper({
            referenceNode: target,
            step: coachMarkStore.currentStep,
          });
        }, 200);
      },
      { fireImmediately: true, equals: comparer.shallow }
    );
  };

  public observeHighlightTarget = () => {
    this.coachMarkHighlightTarget?.();
    const resizeBox = debounce(() => {
      const name = coachMarkStore.currentStep?.name;
      const target = coachMarkStore.getHighlightTarget(name) || coachMarkStore.getTarget(name);
      if (target && coachMarkStore.currentStep?.highlight === 'bg' && coachMarkStore.currentStep?.backdrop !== 'none') {
        coachMarkStore.setHighlightBoxStyles(coachMarkStore.calcHighlightBoxStyles());
      }
    }, 300);

    const closer = reaction(
      () => {
        const name = coachMarkStore.currentStep?.name;
        return coachMarkStore.getHighlightTarget(name) || coachMarkStore.getTarget(name);
      },
      (value) => {
        resizeBox();
        if (!value) {
          coachMarkStore.setHighlightBoxStyles(null);
        }
      }
    );
    const unsubscribe = globalEventService.addEventListener(EVENT_NAME.RESIZE, debounce(resizeBox, 200));
    this.coachMarkHighlightTarget = () => {
      closer();
      unsubscribe();
    };
  };

  private handleChangeCurrentChannelId = (channelId: CHANNEL_ID) => {
    if (isEmpty(channelId)) return;
    uiState.closeModal(MODAL_NAMES.IMAGE_PREVIEW);
    if (channelId === MAIN_THREAD_CHANNEL_ID) {
      this.handleChangeMainThreadChannelId();
      return;
    }

    if (stores.isDoorayNewsChannelId(channelId)) {
      this.closeSettingPanel();
    }

    if (channelsStore.isSharedChannelId(channelId)) {
      this.handleChangeInterTenantChannelId(channelId);
      return;
    }
    juxt([
      this.actions.channelActions.fetchChannelPostNotice,
      this.actions.slashCommandAppActions.fetchChannelApps,
      (channelId) =>
        this.actions.meteringActions
          .fetchAndSet(channelId)
          .then(() => this.actions.meteringActions.setChannelBanner(channelId)),
      channelsStore.fetchPreferences,
      channelMembersLangStore.fetchAndCache,
      this.actions.channelMembersActions.fetchChannelReadSeq,
      this.actions.channelActions.fetchMailSetting,
    ])(channelId);
    mainDetailUiState.setReplyReturnLog(null);
    meteringStore.setShowBannerType(null);
    this.handleChangeCurrentChannel(channelsStore.getById(channelId));
  };

  @action
  private handleChangeContentsChannelId = () => {
    this.actions.mainEditorActions.handleChangeContentsChannelId();
    if (mainDetailUiState.contentsChannelId !== MAIN_THREAD_CHANNEL_ID) {
      threadChannelLogsStore.clearMainThreadChannelInfo();
      threadChannelStore.clearMainChannelList();
      threadChannelStore.clearUnusedData();
    }
  };

  @action
  private handleChangeCurrentChannel = (channel?: Channel) => {
    if (isNil(channel)) return;
    this.fetchChannelLogs(channel, this.actions.logActions);
    pipe(propOr([], 'members'), this.actions.memberActions.fetchBulkMembers)(channel);
    if (isArchivedChannel(channel) && !channel.displayed) {
      api.channel.updateChannelDisplayed(channel.channelId, true);
    }
  };

  @action
  private handleChangeThreadCurrentChannelId = (channelId: THREAD_CHANNEL_ID) => {
    if (!channelId) {
      return;
    }
    uiState.closeModal(MODAL_NAMES.IMAGE_PREVIEW);
    if (uiState.createThreadChannelBy !== 'editor') {
      this.actions.slashCommandAppActions.fetchChannelApps(channelId);
    }
    juxt([
      this.actions.meteringActions.fetchAndSet,
      channelsStore.fetchPreferences,
      channelMembersLangStore.fetchAndCache,
    ])(channelManager.getMainChannelId(channelId));
    const channel = threadChannelStore.getById(channelId);
    if (channel) {
      this.fetchChannelLogs(channel, this.actions.threadLogActions);
    }
  };

  @action
  private handleChangeThreadContentsChannelId = () => {
    if (uiState.createThreadChannelBy !== 'editor') {
      this.actions.threadMainEditorActions.handleChangeContentsChannelId();
    }
  };

  @action
  private handleChangeScreenCapturable = (contents: { channelId: CHANNEL_ID; screenCapture: boolean }) => {
    this.actions.nativeActions.sendChannelChanged(contents);
  };

  @action
  private fetchChannelLogs = (channel: Channel, logActions: LogActions): Promise<unknown> => {
    if (channelsStore.preventFetchLogs) {
      channelsStore.setPreventFetchLogs(false);
      return Promise.resolve();
    } else {
      if (isUnreadChannel(channel)) {
        return logActions.fetchUnreadLogs(channel);
      }

      return logActions.fetchLatestLogs(channel.channelId);
    }
  };

  private handleChangeMainThreadChannelId = () => {
    GA.writeThread({ action: '모아보기 채널', label: '진입' });
    this.closeSettingPanel();
    uiState.setHasUpdatedThreadChannel(false);
    uiState.setHasUnreadThread(false);
    threadChannelStore.setMainThreadChannelAllLoaded(false);
    threadChannelStore.setMainThreadChannelPage(0);
    this.actions.meteringActions
      .fetchAndSet(MAIN_THREAD_CHANNEL_ID)
      .then(() => this.actions.meteringActions.setChannelBanner(MAIN_THREAD_CHANNEL_ID));
    mainDetailUiState.setReplyReturnLog(null);
    channelLogsStore.setNewLog(null);
    threadChannelLogsStore.setLogFilter('all');
    this.actions.threadChannelActions.fetchThreadMainChannel();
  };

  private closeSettingPanel = () => {
    if (uiState.panelType === PANEL_TYPE.CHANNEL_INFO) {
      uiState.setPanelType(PANEL_TYPE.NONE);
    }
  };

  @action
  private handleChangeInterTenantChannelId = async (channelId: CHANNEL_ID) => {
    this.actions.channelMembersActions.fetchChannelReadSeq(channelId);

    mainDetailUiState.setReplyReturnLog(null);
    meteringStore.setShowBannerType(null);
    await this.actions.channelActions.fetchSharedTenantsList(channelId);
    this.handleChangeInterTenantChannel(channelsStore.getById(channelId));
  };

  @action
  private handleChangeInterTenantChannel = (channel?: Channel) => {
    if (isNil(channel)) return;
    this.actions.channelMembersActions.fetchInterTenantChannelMembers(channel);
    this.fetchChannelLogs(channel, this.actions.logActions);
    if (isArchivedChannel(channel) && !channel.displayed) {
      // TODO v4 API 생성 후 적용
      // this.utils.api.channel.updateChannelDisplayed(channel.channelId, true);
    }
  };

  @action
  private handleIsLoadingLogs = (isLoadingLogs: boolean) => {
    clearTimeout(this.slowFetchingTimer);
    if (isLoadingLogs) {
      this.slowFetchingTimer = window.setTimeout(() => {
        mainDetailUiState.setIsSlowFetchingLogs(true);
      }, 300);

      animationEffectStore.setShow(false);
    } else {
      mainDetailUiState.setIsSlowFetchingLogs(false);
    }
  };
}

export default ObserveActions;
